import { useState } from "react";
import {
  parseToothList,
  serializeToothList,
  toggleToothNumber,
} from "../../utils/jobCard";

const upperTeethA = [18, 17, 16, 15, 14, 13, 12, 11];
const upperTeethB = [21, 22, 23, 24, 25, 26, 27, 28];
const lowerTeethA = [48, 47, 46, 45, 44, 43, 42, 41];
const lowerTeethB = [31, 32, 33, 34, 35, 36, 37, 38];

type ToothNumberPickerProps = {
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
  disabled?: boolean;
  label?: string;
  className?: string;
  showEditLabel?: boolean;
};

const ToothGrid = ({
  teeth,
  onToggle,
  compact,
}: {
  teeth: string[];
  onToggle: (tooth: number) => void;
  compact?: boolean;
}) => {
  const cell = compact
    ? "h-6 w-6 text-[9px]"
    : "h-7 w-7 text-[10px] md:h-8 md:w-8 md:text-xs";

  const renderRow = (row: number[]) => (
    <div className="flex w-full items-center justify-evenly gap-0.5 py-0.5">
      {row.map((tooth) => {
        const selected = teeth.includes(String(tooth));
        return (
          <button
            key={tooth}
            type="button"
            onClick={() => onToggle(tooth)}
            aria-pressed={selected}
            className={`flex items-center justify-center rounded-md font-medium transition-colors ${cell} ${
              selected
                ? "bg-[#2fa9bf] text-white"
                : "bg-white text-[#4A4A48] ring-1 ring-[#D8D8D2] hover:bg-[#F3FBFC]"
            }`}
          >
            {tooth}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="rounded-lg border border-[#C9D4D6] bg-white p-2">
      <div className="flex">
        {renderRow(upperTeethA)}
        <div className="mx-1.5 w-px shrink-0 self-stretch bg-[#2fa9bf]" />
        {renderRow(upperTeethB)}
      </div>
      <div className="my-1.5 h-px bg-[#2fa9bf]" />
      <div className="flex">
        {renderRow(lowerTeethA)}
        <div className="mx-1.5 w-px shrink-0 self-stretch bg-[#2fa9bf]" />
        {renderRow(lowerTeethB)}
      </div>
      {compact ? (
        <p className="mt-1.5 text-center text-[11px] text-[#8A8A85]">
          {teeth.length ? `${teeth.length} selected` : "Click teeth to select"}
        </p>
      ) : null}
    </div>
  );
};

export const ToothChips = ({
  value,
  onRemove,
}: {
  value?: string | null;
  onRemove?: (tooth: string) => void;
}) => {
  const teeth = parseToothList(value);
  if (!teeth.length) {
    return <span className="text-[14px] text-[#8A8A85]">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {teeth.map((tooth) => (
        <span
          key={tooth}
          className="inline-flex items-center gap-0.5 rounded-md bg-white px-1.5 py-0.5 text-[13px] font-medium text-[#2a7d8c] ring-1 ring-[#7ECAD8]"
        >
          {tooth}
          {onRemove ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onRemove(tooth);
              }}
              className="ml-0.5 text-[#2fa9bf] hover:text-[#1a6d7c]"
              aria-label={`Remove tooth ${tooth}`}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
};

const ToothNumberPicker = ({
  value,
  onChange,
  compact = false,
  disabled = false,
  label = "Select teeth",
  className = "",
  showEditLabel = false,
}: ToothNumberPickerProps) => {
  const [draft, setDraft] = useState("");
  const teeth = parseToothList(value);

  const handleToggle = (tooth: number) => {
    if (disabled) return;
    onChange(toggleToothNumber(value, tooth));
  };

  const handleRemove = (tooth: string) => {
    if (disabled) return;
    onChange(
      serializeToothList(teeth.filter((item) => item !== String(tooth))),
    );
  };

  const addToothFromDraft = () => {
    if (disabled) return;
    const next = draft.replace(/\D/g, "");
    if (!next) return;
    if (!teeth.includes(next)) {
      onChange(serializeToothList([...teeth, next]));
    }
    setDraft("");
  };

  const chips = (
    <ToothChips value={value} onRemove={disabled ? undefined : handleRemove} />
  );

  const grid = (
    <ToothGrid teeth={teeth} onToggle={handleToggle} compact={compact} />
  );

  if (!compact) {
    return (
      <div className={`flex w-full items-stretch gap-3 ${className}`}>
        <div className="w-[30%] min-w-0 rounded-lg border border-[#C9D4D6] bg-white p-2">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold text-[#2fa9bf]">
              {teeth.length ? `${teeth.length} selected` : "No teeth selected"}
            </p>
            {showEditLabel ? (
              <span className="shrink-0 text-[11px] font-medium text-[#2fa9bf]">
                Edit selection
              </span>
            ) : null}
          </div>
          {teeth.length ? (
            chips
          ) : (
            <span className="text-xs text-[#8A8A85]">
              Click teeth on the chart
            </span>
          )}
        </div>
        <div className="w-[70%] min-w-0">{grid}</div>
      </div>
    );
  }

  return (
    <div className={`flex w-full min-w-0 flex-col gap-1 ${className}`}>
      {teeth.length ? chips : null}
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={disabled}
        value={draft}
        placeholder={label || "Tooth no. + Enter"}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addToothFromDraft();
          }
        }}
        className="w-full rounded-lg border border-[#C9D4D6] bg-white px-1.5 py-1.5 text-[14px] text-[#4A4A48] outline-none placeholder:text-[#8A8A85] focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 disabled:cursor-not-allowed disabled:opacity-60"
      />
    </div>
  );
};

export default ToothNumberPicker;
